import React from "react";
import ReactDOM from "react-dom";
import {
  Snackbar,
  Slide,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded"; 
import { useTheme, useMediaQuery, SlideProps } from "@mui/material";
import themestyle from "../theme";

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="down" />;
}

type SnackbarType = "success" | "error" | "info";

interface CustomSnackbarProps {
  open: boolean;
  message: string;
  type: SnackbarType;
  onClose: () => void;
  autoHideDuration?: number;
  offsetTop?: number;
  instanceKey?: React.Key;
}

const CustomSnackbar: React.FC<CustomSnackbarProps> = ({
  open,
  message,
  type,
  onClose,
  autoHideDuration = 3000,
  offsetTop,
  instanceKey,
}) => {

  const styles = {
    success: {
      iconColor: themestyle.colors.success,
      gradient: "linear-gradient(90deg, #F0FFF8 0%, #FFFFFF 100%)",
      Icon: CheckCircleIcon,
    },
    error: {
      iconColor: themestyle.colors.error,
      gradient: "linear-gradient(90deg, #FFF6F6 0%, #FFFFFF 100%)",
      Icon: CancelIcon,
    },
    info: {
      iconColor: themestyle.colors.link,
      gradient: "linear-gradient(90deg, #EAF2FF 0%, #FFFFFF 100%)",
      Icon: WarningAmberRoundedIcon,
    },
  } as const;

  const current = styles[type] ?? styles.info;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleClose = (_e?: any, reason?: string) => {
    if (reason === "clickaway") return;
    onClose();
  };

  const snackbarContent = (
    <Snackbar
      key={instanceKey}
      open={open}
      onClose={handleClose}
      autoHideDuration={autoHideDuration}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      TransitionComponent={SlideTransition}
      sx={(theme) => ({
        position: "fixed",
        zIndex: theme.zIndex.snackbar + 1000,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        top: offsetTop ?? theme.spacing(3),
        left: "50%",
        transform: "translateX(-50%)",
        right: "auto",
      })}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: current.gradient,
          borderRadius: "12px",
          border: "1px solid #E0E0E0",
          boxShadow:
            "0px 4px 10px rgba(0,0,0,0.15), 0px 6px 20px rgba(0,0,0,0.10), 0px 16px 24px rgba(0,0,0,0.08)",
          padding: isMobile ? "10px" : "12px 12px 12px 16px",
          minWidth: isMobile ? "215px" : "280px",
          minHeight: "56px",
          gap: isMobile ? "8px" : "10px",
        }}
      >

        <Box sx={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <Box
            sx={{
              width: isMobile ? "28px" : "32px",
              height: isMobile ? "28px" : "32px",
              borderRadius: "50%",
              backgroundColor: "#F4F4F4",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >

            <current.Icon sx={{ color: current.iconColor, fontSize: 22 }} />
          </Box>

          <Typography
            sx={{
              color: themestyle.colors.black,
              fontWeight: 600,
              fontSize: isMobile ? "10px" : "16px",
              lineHeight: isMobile ? "20px" : "22px",
              letterSpacing: "0.01em",
              fontFamily: isMobile ? "Poppins, Inter, Roboto, sans-serif" : undefined,
              maxWidth: isMobile ? 200 : 420,
              whiteSpace: "normal",
              overflowWrap: "anywhere",
            }}
            title={message}
          >
            {message}
          </Typography>
        </Box>

        <Box
          sx={{
            width: "1px",
            height: "100%",
            backgroundColor: "#EAEAEA",
            opacity: 1,
            ml: "auto",
            mr: "4px",
          }}
        />

        <IconButton
          aria-label="Close notification"
          onClick={handleClose}
          size="small"
          sx={{
            width: isMobile ? "24px" : "32px",
            height: isMobile ? "24px" : "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#7F8C8D",
            "&:hover": { background: "transparent" },
          }}
        >
          <CloseIcon sx={{ fontSize: isMobile ? 12 : 22 }} />
        </IconButton>
      </Box>
    </Snackbar>
  );

  return ReactDOM.createPortal(snackbarContent, document.body);
};
export default CustomSnackbar;
